import { Controller } from "@hotwired/stimulus"
import { escapeHtml } from "../escape_html"

// Recording foods you will not eat, or would rather not.
//
// Searches the SHARED VOCABULARY (PreferenceTerm), not the food library. The
// library holds 273 rows for "chicken" and 166 for "cheese" — right for a
// recipe picker, since raw thigh and boiled feet have different macros, and
// wrong here: a preference is one idea.
//
// The vocabulary is seeded and grows when somebody adds a term, so "shrimp",
// "shrimps" and "prawns" converge on one row rather than five hundred people
// each inventing a spelling. Which foods a term covers gets answered once,
// later, rather than per user.
export default class extends Controller {
  static targets = ["input", "results", "chips", "fields", "empty"]
  static values = { kind: String, url: String }

  connect() {
    this.items = this.readExisting()
    this.debounce = null
    this.render()

    // A click anywhere else closes the results. Blur alone is not enough:
    // clicking a heading or empty page moves focus nowhere, so no blur fires
    // and the panel stays open behind whatever was clicked.
    this.onOutsideClick = (event) => {
      if (this.resultsTarget.hidden) return
      if (this.element.contains(event.target)) return

      this.resultsTarget.hidden = true
    }
    document.addEventListener("mousedown", this.onOutsideClick, true)
  }

  disconnect() {
    clearTimeout(this.debounce)
    document.removeEventListener("mousedown", this.onOutsideClick, true)
  }

  // Chips rendered server-side on a re-render carry their own label, so a
  // rejected form does not lose what was already picked.
  readExisting() {
    return Array.from(this.element.querySelectorAll("[data-existing]"))
                .map((node) => ({ label: node.dataset.label }))
  }

  search() {
    clearTimeout(this.debounce)
    this.debounce = setTimeout(() => this.fetchTerms(), 180)
  }

  async fetchTerms() {
    const query = this.inputTarget.value.trim()

    if (query.length < 2) {
      this.resultsTarget.hidden = true
      return
    }

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`,
                                   { headers: { Accept: "application/json" } })
      this.renderResults(await response.json(), query)
    } catch {
      // A failed search must not stop someone recording an allergy — the
      // add-your-own row is always there.
      this.renderResults([], query)
    }
  }

  // Enter takes the first result, or adds what was typed. The form must not
  // submit — a field inside a form submits it on enter by default, which
  // would post the step half-answered.
  enter(event) {
    if (event.key !== "Enter") return

    event.preventDefault()

    const first = this.resultsTarget.querySelector("[data-label]")
    this.add(first && !this.resultsTarget.hidden ? first.dataset.label : this.inputTarget.value)
  }

  // Same markup as the food picker and the searchable select — combobox-option
  // plus the utilities they use. Not a second set of result styles.
  renderResults(terms, query) {
    const row = "combobox-option flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink"

    const rows = terms.map((term) =>
      `<button type="button" class="${row}" data-action="food-tags#choose"
               data-label="${this.escape(term.name)}">
         ${this.escape(term.name)}
       </button>`
    )

    // Offered only when nothing matched exactly.
    //
    // "Add X anyway" was the first wording and read as a warning — as though
    // adding your own were a lesser thing to do. It is not: the vocabulary
    // grows this way, and someone recording an allergy we have no word for
    // should not feel they are working around the app.
    const exact = terms.some((term) => term.name.toLowerCase() === query.toLowerCase())

    if (!exact) {
      rows.push(`<button type="button" class="${row} border-t border-line text-ink-soft"
                   data-action="food-tags#choose" data-label="${this.escape(query)}">
                   Add <span class="font-semibold text-ink">${this.escape(query)}</span>
                 </button>`)
    }

    this.resultsTarget.innerHTML = rows.join("")
    this.resultsTarget.hidden = false
  }

  choose(event) {
    this.add(event.currentTarget.dataset.label)
  }

  toggleSuggestion(event) {
    const label = event.currentTarget.dataset.label
    const existing = this.items.findIndex((item) => this.same(item.label, label))

    if (existing >= 0) {
      this.items.splice(existing, 1)
    } else {
      this.items.push({ label })
    }

    this.render()
  }

  add(value) {
    const label = value.trim().replace(/\s+/g, " ")

    this.inputTarget.value = ""
    this.resultsTarget.hidden = true

    if (!label) return
    if (this.items.some((item) => this.same(item.label, label))) return

    this.items.push({ label })
    this.render()
  }

  remove(event) {
    this.items.splice(Number(event.currentTarget.dataset.index), 1)
    this.render()
  }

  same(a, b) { return a.toLowerCase() === b.toLowerCase() }

  // The labels the suggestion row displays on its own, so the chips list does
  // not repeat them.
  suggestionLabels() {
    return Array.from(this.element.querySelectorAll("[data-suggestion]"))
                .map((button) => button.dataset.label)
  }

  render() {
    this.renderChips()
    this.renderFields()
    this.renderSuggestionStates()

    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = this.items.length > 0
    }
  }

  // Only the things NOT already shown as a lit suggestion. A ticked
  // suggestion appearing again as a chip below said the same thing twice: the
  // button is already lit, and the list underneath repeated it.
  renderChips() {
    const suggested = this.suggestionLabels()

    this.chipsTarget.innerHTML = this.items.filter((item) =>
      !suggested.some((label) => this.same(label, item.label))
    ).map((item) => {
      // The index into the FULL list, since remove() splices from that.
      const index = this.items.indexOf(item)

      return `
        <span class="chip gap-1.5 bg-brand-soft py-1 pl-2.5 pr-1.5 text-xs text-brand-deep">
          ${this.escape(item.label)}
          <button type="button" data-action="food-tags#remove" data-index="${index}"
                  class="cursor-pointer px-0.5 text-sm leading-none opacity-60 hover:opacity-100"
                  aria-label="Remove ${this.escape(item.label)}">&times;</button>
        </span>
      `
    }).join("")
  }

  renderFields() {
    this.fieldsTarget.innerHTML = this.items.map((item) =>
      `<input type="hidden" name="food_preference[${this.kindValue}s][]"
              value="${this.escape(item.label)}">`
    ).join("")
  }

  renderSuggestionStates() {
    this.element.querySelectorAll("[data-suggestion]").forEach((button) => {
      const picked = this.items.some((item) => this.same(item.label, button.dataset.label))
      button.setAttribute("aria-pressed", picked ? "true" : "false")
    })
  }

  // One shared implementation — see escape_html.js.
  escape(text) { return escapeHtml(text) }
}
